/**
 * DV-501 — client-scope management for a Space scoped to specific clients.
 *
 * Purely presentational, and deliberately so: it MUTATES NOTHING. Picking
 * clients and confirming a conversion only stage intent on the editor's draft,
 * and the takeover's "Save space" is the single write. One save means one
 * `updateSpace` call, which is also what the backend wants — it accepts only
 * one shape of client update per request (`clients` XOR `addClientIds`, and
 * neither alongside `appliesToAllClients: true`).
 *
 * There is no "Add clients" button: a selection in the picker IS the staged
 * change, so a separate confirm step would just be a second button that doesn't
 * persist anything either.
 *
 * Rendered only for an existing, one-or-more-clients Space — a Space that
 * already covers every client has nothing to add to and nothing left to convert.
 */
import React, { useState } from "react";
import { Banner } from "@jsq/ds/core";
import { toBareReferenceId } from "#common/globalId";
import { formatNames } from "#helpers/format-names";
import { ClientPicker, clientSelectionFrom, type ClientSelection } from "./ClientPicker";
import * as styles from "./styles_vanilla.css";

/** Shown under the client picker on New space and Edit space (DV-726). */
export const CLIENT_SCOPE_HELPER_TEXT =
  "You can add clients here at any time after the space is created.";

export function ClientScopeHelper() {
  return (
    <div className={styles.help} data-testid="client-scope-helper">
      {CLIENT_SCOPE_HELPER_TEXT}
    </div>
  );
}

export type ClientScopePanelProps = {
  /** Name of the space, for the pending-conversion copy. */
  spaceName: string;
  /** Clients already saved on this space. Read-only — removal is out of scope. */
  clients: string[];
  /** Display names for {@link clients}, keyed by id. Falls back to the id when missing. */
  clientNames?: Record<string, string>;
  /** Clients staged for addition, not yet saved. */
  staged: ClientSelection;
  onStageClients: (selection: ClientSelection) => void;
  /**
   * True once a conversion has been confirmed and is awaiting save. Reported
   * here for display only — the Client scope radio above owns both staging it
   * and undoing it, so this panel offers no control of its own.
   */
  convertStaged: boolean;
  /**
   * Mount the `ClientSearch` typeahead. It fetches its own arenas and therefore
   * needs an `ApolloProvider` above it, so the presentational path (stories,
   * component tests) leaves it off — same opt-in as `UploadDocumentModal`'s
   * `referenceSearch`.
   */
  clientSearch?: boolean;
};

export function ClientScopePanel({
  spaceName,
  clients,
  clientNames,
  staged,
  onStageClients,
  convertStaged,
  clientSearch = false,
}: ClientScopePanelProps) {
  /** Names of picks dropped for already being on {@link clients} — see {@link stageNewClients}. */
  const [duplicatePicks, setDuplicatePicks] = useState<string[] | null>(null);

  /**
   * `ClientSearch`'s dedupe guard only compares a pick against `staged`, not
   * the saved `clients`, so a re-pick already on the space must be caught here.
   */
  const stageNewClients = (selection: ClientSelection) => {
    const kept: string[] = [];
    const dropped: string[] = [];
    selection.globalIds.forEach((globalId) => {
      const bareId = toBareReferenceId(globalId);
      if (clients.includes(bareId)) dropped.push(clientNames?.[bareId] ?? bareId);
      else kept.push(globalId);
    });
    setDuplicatePicks(dropped.length > 0 ? dropped : null);
    onStageClients(clientSelectionFrom(kept));
  };

  // Once a conversion is staged the client list is on its way out, so offering
  // to add to it would be staging two contradictory changes into one save.
  // Undoing is switching Client scope back to "One or more clients" above.
  if (convertStaged) {
    return (
      <div className={styles.field} data-testid="space-client-scope">
        <span className={styles.fieldLabel}>Clients</span>
        <Banner
          variant="warning"
          title="Will apply to all clients when you save"
          description={`${spaceName} will become visible to every client, and its current client list will be cleared. Nothing has changed yet — save to apply it, or switch Client scope back to "One or more clients" to undo.`}
        />
      </div>
    );
  }

  return (
    <div className={styles.field} data-testid="space-client-scope">
      <span className={styles.fieldLabel}>Clients</span>

      {clients.length > 0 ? (
        <div className={styles.clientChips}>
          {clients.map((c) => (
            <span key={c} className={styles.clientChip}>
              {clientNames?.[c] ?? c}
            </span>
          ))}
        </div>
      ) : (
        <div className={styles.clientEmpty}>This space isn’t visible to any client yet.</div>
      )}

      {clientSearch ? (
        <>
          <ClientPicker
            value={staged}
            placeholder="Search for clients to add"
            testId="space-add-client-search"
            onChange={stageNewClients}
          />
          {duplicatePicks && (
            <div className={styles.help} data-testid="duplicate-clients-note">
              {`${formatNames(duplicatePicks)} ${
                duplicatePicks.length === 1 ? "is" : "are"
              } already in this space’s client list.`}
            </div>
          )}
          <ClientScopeHelper />
        </>
      ) : (
        <div className={styles.help}>
          Client search isn’t available here. Open this space from the Vault to add clients.
        </div>
      )}
    </div>
  );
}
